import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

import { MaxWidths, createMaxWidths } from './createMaxWidths';
import * as CustomColors from './colors';
import * as Units from './units';
import {
  listItemTextClasses,
  alertClasses,
  inputBaseClasses,
  outlinedInputClasses,
} from '@mui/material';

interface CustomSpacing {
  formRow: number;
  navHeight: number;
  navHeightLg: number;
}
interface CustomSpacingOptions {
  formRow?: number;
  navHeight?: number;
  navHeightLg?: number;
}

declare module '@mui/material/styles/createPalette' {
  interface TypeText {
    hint: string;
  }

  interface Palette {
    warning: PaletteColor;
    ok: PaletteColor;
    orange: PaletteColor;
    white: PaletteColor;
    positive: PaletteColor;
    negative: PaletteColor;
  }

  // `createMuiTheme`'s parameter, used to customize/extend theme
  interface PaletteOptions {
    ok: PaletteColor;
    orange: PaletteColor;
    white: PaletteColor;
    positive: PaletteColor;
    negative: PaletteColor;
  }
}

declare module '@mui/material/styles/createTheme' {
  interface Theme {
    maxWidths: MaxWidths;
    customSpacing: CustomSpacing;
  }

  interface ThemeOptions {
    maxWidths?: MaxWidths;
    customSpacing?: CustomSpacingOptions;
  }
}

export function createMyTheme(options: ThemeOptions): Theme {
  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    maxWidths: createMaxWidths(),
    ...options,
    palette: {
      primary: CustomColors.primary,
      secondary: CustomColors.secondary,
      error: CustomColors.negative,
      grey: CustomColors.grey,
      ok: CustomColors.positive,
      warning: CustomColors.warning,
      orange: CustomColors.orange,
      white: CustomColors.white,
      positive: CustomColors.positive,
      negative: CustomColors.negative,
      text: { hint: 'rgba(0, 0, 0, 0.38)' },
      ...options.palette,
    },
    typography: {
      fontFamily: `'Source Sans Pro', 'Helvetica', 'Arial', 'sans-serif'`, // TODO take it from fonts
      fontSize: 14,
      ...options.typography,
    },
    customSpacing: {
      formRow: 1.4,
      navHeight: 56,
      navHeightLg: 64,
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          standardError: {
            backgroundColor: CustomColors.negative.light,
            [`& .${alertClasses.icon}`]: {
              color: CustomColors.negative.main,
            },
          },
          standardWarning: {
            backgroundColor: CustomColors.warning.light,
            [`& .${alertClasses.icon}`]: {
              color: CustomColors.warning.main,
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            whiteSpace: 'nowrap',
          },
          outlinedInherit: {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            [`& .${listItemTextClasses.root}`]: {
              marginTop: '4px',
              marginBottom: '4px',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            [`& .${outlinedInputClasses.root}.${inputBaseClasses.sizeSmall}`]: {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontSize: '1rem',
            lineHeight: '1.5',
            color: CustomColors.grey[700],
            backgroundColor: CustomColors.white.main,
          },
          img: {
            verticalAlign: 'middle',
          },
          a: {
            color: CustomColors.grey[700],
          },
        },
      },
    },
  });
}

/**
 * @link https://mui.com/api/button/#inheritance
 */
export function createMyThemeNext(options: ThemeOptions): Theme {
  return createTheme({
    maxWidths: createMaxWidths(),
    ...options,
    palette: {
      primary: CustomColors.primaryNext,
      secondary: CustomColors.secondary,
      error: CustomColors.negative,
      grey: CustomColors.grey,
      ok: CustomColors.positive,
      warning: CustomColors.warning,
      orange: CustomColors.orange,
      white: CustomColors.white,
      positive: CustomColors.positive,
      negative: CustomColors.negative,
      text: { hint: 'rgba(0, 0, 0, 0.38)' },
      ...options.palette,
    },
    typography: {
      fontFamily: `'Source Sans Pro', 'Helvetica', 'Arial', 'sans-serif'`,
      fontSize: 14,
      ...options.typography,
    },
    customSpacing: {
      formRow: 1.4,
      navHeight: 56,
      navHeightLg: 64,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            fontSize: Units.rem(1),
            textTransform: 'none',
          },
          containedPrimary: {
            '&:hover': {
              backgroundColor:
                CustomColors.ButtonsPrimaryContainedNext.backgroundHover,
            },
            '&:disabled': {
              color: CustomColors.grey[100],
              backgroundColor:
                CustomColors.ButtonsPrimaryContainedNext.backgroundDisabled,
            },
          },
          outlinedPrimary: {
            color: CustomColors.primaryNext.main,
            backgroundColor: CustomColors.ButtonsPrimaryOutlinedNext.background,
            borderColor: CustomColors.ButtonsPrimaryOutlinedNext.border,
            '&:hover': {
              borderColor: CustomColors.ButtonsPrimaryOutlinedNext.border,
              backgroundColor:
                CustomColors.ButtonsPrimaryOutlinedNext.backgroundHover,
            },
            '&:disabled': {
              color: CustomColors.grey[400],
              backgroundColor:
                CustomColors.ButtonsPrimaryOutlinedNext.backgroundDisabled,
            },
          },
          sizeSmall: {
            fontSize: Units.rem(0.875),
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontSize: '1rem',
            lineHeight: '1.5',
            color: CustomColors.grey[700],
            backgroundColor: CustomColors.white.main,
          },
          img: {
            verticalAlign: 'middle',
          },
          a: {
            color: CustomColors.grey[700],
          },
        },
      },
    },
  });
}

const theme = createMyTheme({});
export const themeNext = createMyThemeNext({});

export { theme };
